import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy, ExternalLink as ExternalIcon } from "lucide-react";
import { cx } from "../../lib/cx.js";
import { Button, type ButtonSize, type ButtonVariant } from "./Button.js";

/** Copia texto al portapapeles y confirma visualmente (y por aria-live). */
export function CopyButton({
  text, label = "Copiar", copiedLabel = "Copiado", variant = "secondary", size = "sm", className,
}: {
  text: string;
  label?: string;
  copiedLabel?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setFailed(false);
    } catch {
      // Sin permiso de portapapeles (http, iframe): el usuario puede copiar a mano.
      setFailed(true);
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setCopied(false);
      setFailed(false);
    }, 2500);
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={copy}
      className={className}
      icon={copied ? <Check className="size-4" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
    >
      <span aria-live="polite">{failed ? "No se pudo copiar" : copied ? copiedLabel : label}</span>
    </Button>
  );
}

/**
 * Enlace a un sitio externo: pestaña nueva, sin opener ni Referer (no filtra a terceros
 * desde qué caso o búsqueda se llegó).
 */
export function ExternalLink({
  href, children, className, showIcon = true, title,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  showIcon?: boolean;
  title?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      title={title}
      className={cx("inline-flex items-center gap-1 text-accent hover:underline", className)}
    >
      {children}
      {showIcon && <ExternalIcon className="size-3.5 shrink-0" aria-hidden="true" />}
      <span className="sr-only"> (se abre en una pestaña nueva)</span>
    </a>
  );
}

/**
 * Medidor de uso contra un límite (cuota mensual, usuarios). Pista y relleno del
 * mismo tono; al acercarse al límite cambia a alerta y el texto lo dice explícito.
 */
export function Meter({ value, max, label }: { value: number; max: number | null; label: string }) {
  if (!max) {
    return <p className="text-[12.5px] text-subtle">{label}: sin límite</p>;
  }
  const ratio = Math.min(1, value / max);
  const tone = ratio >= 1 ? "bg-danger" : ratio >= 0.85 ? "bg-warning" : "bg-accent";
  const track = ratio >= 1 ? "bg-danger/20" : ratio >= 0.85 ? "bg-warning/20" : "bg-accent/20";
  return (
    <div className="space-y-1">
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        className={cx("h-2 w-full overflow-hidden rounded-full", track)}
      >
        <div className={cx("h-full rounded-full", tone)} style={{ width: `${Math.max(2, ratio * 100)}%` }} />
      </div>
    </div>
  );
}
